/**
 * HalaAvatar Component - Animated AI Assistant Avatar
 * 
 * States:
 * - idle: subtle breathing, occasional blink, slight movement
 * - listening: glowing pulse, attentive tilt
 * - thinking: animated dots/pulse, floating motion
 * - speaking: head movement, soft highlight
 * - success: gentle green glow pulse
 */

import { useState, useEffect, memo } from 'react';

const COLORS = {
  primary: '#1D8F4E',
  primaryLight: '#E8F5EE',
  gold: '#C5A572',
  secondary: '#1B3A6B',
};

// Animation keyframes as CSS string
const avatarAnimations = `
  @keyframes hala-breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.02); }
  }
  
  @keyframes hala-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-3px); }
  }
  
  @keyframes hala-pulse-glow {
    0%, 100% { 
      box-shadow: 0 0 0 0 rgba(29, 143, 78, 0.4);
    }
    50% { 
      box-shadow: 0 0 0 8px rgba(29, 143, 78, 0);
    }
  }
  
  @keyframes hala-success-glow {
    0%, 100% { 
      box-shadow: 0 0 0 0 rgba(29, 143, 78, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    50% { 
      box-shadow: 0 0 0 12px rgba(29, 143, 78, 0), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  }
  
  @keyframes hala-thinking-dots {
    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1); }
  }
  
  @keyframes hala-tilt-listen {
    0%, 100% { transform: rotate(0deg); }
    50% { transform: rotate(-3deg); }
  }
  
  @keyframes hala-speak-move {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    25% { transform: translateY(-1px) rotate(1deg); }
    75% { transform: translateY(1px) rotate(-1deg); }
  }
  
  @keyframes hala-ring-rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes hala-soft-pulse {
    0%, 100% { opacity: 0.6; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.05); }
  }
`;

// Avatar state types
const AVATAR_STATES = {
  IDLE: 'idle',
  LISTENING: 'listening',
  THINKING: 'thinking',
  SPEAKING: 'speaking',
  SUCCESS: 'success',
};

const HalaAvatar = memo(function HalaAvatar({ 
  state = AVATAR_STATES.IDLE, 
  size = 48,
  showStatusDot = true,
  className = '',
  style = {}
}) {
  const [isAnimating, setIsAnimating] = useState(true);

  // Inject animations on mount
  useEffect(() => {
    const styleId = 'hala-avatar-animations';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = avatarAnimations;
      document.head.appendChild(styleSheet);
    }
    return () => {
      // Keep animations in DOM for other instances
    };
  }, []);

  // Pause animations when tab is not visible
  useEffect(() => {
    const handleVisibility = () => {
      setIsAnimating(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const getContainerAnimation = () => {
    if (!isAnimating) return 'none';
    
    switch (state) {
      case AVATAR_STATES.IDLE:
        return 'hala-breathe 4s ease-in-out infinite';
      case AVATAR_STATES.LISTENING:
        return 'hala-tilt-listen 2s ease-in-out infinite';
      case AVATAR_STATES.THINKING:
        return 'hala-float 2s ease-in-out infinite';
      case AVATAR_STATES.SPEAKING:
        return 'hala-speak-move 0.8s ease-in-out infinite';
      case AVATAR_STATES.SUCCESS:
        return 'hala-breathe 3s ease-in-out infinite';
      default:
        return 'hala-breathe 4s ease-in-out infinite';
    }
  };

  const getGlowAnimation = () => {
    if (!isAnimating) return 'none';
    
    switch (state) {
      case AVATAR_STATES.LISTENING:
      case AVATAR_STATES.THINKING:
        return 'hala-pulse-glow 2s ease-in-out infinite';
      case AVATAR_STATES.SUCCESS:
        return 'hala-success-glow 1.5s ease-in-out infinite';
      default:
        return 'none';
    }
  };

  const getBorderColor = () => {
    switch (state) {
      case AVATAR_STATES.SUCCESS:
        return COLORS.primary;
      case AVATAR_STATES.LISTENING:
        return COLORS.gold;
      case AVATAR_STATES.THINKING:
        return COLORS.secondary;
      default:
        return COLORS.primary;
    }
  };

  const getStatusDotColor = () => {
    switch (state) {
      case AVATAR_STATES.SUCCESS:
        return '#10B981';
      case AVATAR_STATES.THINKING:
        return COLORS.gold;
      case AVATAR_STATES.LISTENING:
        return '#3B82F6';
      default:
        return COLORS.primary;
    }
  };

  const statusDotSize = Math.max(10, size * 0.28);
  const borderWidth = Math.max(2, size * 0.04);

  return (
    <div 
      className={`hala-avatar-container ${className}`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
    >
      {/* Outer glow ring for thinking/listening states */}
      {(state === AVATAR_STATES.THINKING || state === AVATAR_STATES.LISTENING) && isAnimating && (
        <div
          style={{
            position: 'absolute',
            width: size + 16,
            height: size + 16,
            borderRadius: '50%',
            border: `2px solid ${state === AVATAR_STATES.THINKING ? COLORS.secondary : COLORS.gold}`,
            opacity: 0.3,
            animation: 'hala-ring-rotate 8s linear infinite',
            borderStyle: 'dashed',
          }}
        />
      )}
      
      {/* Main avatar container */}
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          overflow: 'hidden',
          border: `${borderWidth}px solid ${getBorderColor()}`,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'white',
          animation: `${getContainerAnimation()}, ${getGlowAnimation()}`,
          transition: 'border-color 0.3s ease',
        }}
      >
        <img 
          src="/hala-avatar-ui.png" 
          alt="هلا" 
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            objectPosition: 'center top',
            pointerEvents: 'none',
          }} 
        />
      </div>
      
      {/* Thinking dots indicator */}
      {state === AVATAR_STATES.THINKING && isAnimating && (
        <div
          style={{
            position: 'absolute',
            bottom: -8,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 3,
          }}
        >
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: COLORS.secondary,
                animation: `hala-thinking-dots 1.2s ease-in-out infinite`,
                animationDelay: `${i * 0.15}s`,
              }}
            />
          ))}
        </div>
      )}
      
      {/* Status dot */}
      {showStatusDot && (
        <span 
          style={{ 
            position: 'absolute', 
            bottom: state === AVATAR_STATES.THINKING ? 0 : -2, 
            right: -2, 
            width: statusDotSize, 
            height: statusDotSize, 
            backgroundColor: getStatusDotColor(),
            border: `${Math.max(2, borderWidth)}px solid white`, 
            borderRadius: '50%',
            transition: 'background-color 0.3s ease',
            animation: state === AVATAR_STATES.SUCCESS && isAnimating 
              ? 'hala-soft-pulse 1s ease-in-out infinite' 
              : 'none',
          }}
        />
      )}
    </div>
  );
});

// Export states for external use
HalaAvatar.STATES = AVATAR_STATES;

export default HalaAvatar;
export { AVATAR_STATES };
